package dev.wyns.ventrova.data.model

import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDateTime

private const val MINUTES_PER_DAY = 24 * 60

/** Minutes after midnight. [end] can exceed 24:00 when the range runs past midnight. */
data class TimeRange(val start: Int, val end: Int) {
    init {
        require(start in 0..MINUTES_PER_DAY && end > start) { "Invalid time range $start-$end" }
    }
}

/** Weekly opening hours. Days without ranges are closed. */
data class OpeningHours(
    val week: Map<DayOfWeek, List<TimeRange>>,
) {
    fun rangesOn(day: DayOfWeek): List<TimeRange> = week[day].orEmpty()

    val isAlwaysOpen: Boolean
        get() = DayOfWeek.entries.all { day -> rangesOn(day).any { it.start == 0 && it.end >= MINUTES_PER_DAY } }

    fun statusAt(dateTime: LocalDateTime): OpenStatus {
        if (isAlwaysOpen) return OpenStatus.AlwaysOpen

        val today = dateTime.dayOfWeek
        val minute = dateTime.hour * 60 + dateTime.minute

        val openRange = rangesOn(today).firstOrNull { minute >= it.start && minute < it.end }
            ?: rangesOn(today.minusDays(1))
                .firstOrNull { it.end > MINUTES_PER_DAY && minute < it.end - MINUTES_PER_DAY }
                ?.let { TimeRange(0, it.end - MINUTES_PER_DAY) }
        if (openRange != null) return OpenStatus.Open(closesAt = openRange.end % MINUTES_PER_DAY)

        rangesOn(today).filter { it.start > minute }.minOfOrNull { it.start }?.let {
            return OpenStatus.Closed(opensAt = it, opensInDays = 0, opensOn = today)
        }
        for (daysAhead in 1..7) {
            val day = today.plusDays(daysAhead)
            rangesOn(day).minOfOrNull { it.start }?.let {
                return OpenStatus.Closed(opensAt = it, opensInDays = daysAhead, opensOn = day)
            }
        }
        return OpenStatus.Closed(opensAt = null, opensInDays = null, opensOn = null)
    }
}

sealed interface OpenStatus {
    val isOpen: Boolean

    data object AlwaysOpen : OpenStatus {
        override val isOpen = true
    }

    /** [closesAt] in minutes after midnight. */
    data class Open(val closesAt: Int) : OpenStatus {
        override val isOpen = true
    }

    /** [opensAt] in minutes after midnight on [opensOn]; all null when it never opens again. */
    data class Closed(val opensAt: Int?, val opensInDays: Int?, val opensOn: DayOfWeek?) : OpenStatus {
        override val isOpen = false
    }
}

fun DayOfWeek.plusDays(days: Int): DayOfWeek = DayOfWeek.entries[(ordinal + days).mod(7)]

fun DayOfWeek.minusDays(days: Int): DayOfWeek = plusDays(-days)

/** Formats minutes after midnight as `HH:MM`. */
fun formatMinutes(minutes: Int): String {
    val m = minutes % MINUTES_PER_DAY
    return "${(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}"
}
